import { Spacing, Typography } from '@persona-id/design-tokens';
import { css } from 'styled-components';

import { printMixins } from 'dashboard/lib/printMixins';
import {
  isActive,
  isDisabled,
  isFocused,
  isHovered,
  isNotDisabled,
} from 'design-system/lib/pseudoClass';
import ErrorProps from 'design-system/types/ErrorProps';
import PseudoClassProps from 'design-system/types/PseudoClassProps';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';

export type InputSizes = 'md' | 'sm';

export interface InputMixinProps extends ErrorProps, PseudoClassProps {
  $size?: InputSizes;
}

/**
 * Provides input-like hover and focus outline effects. Can also be applied to
 * non-input elements (like divs) to give input-like styles.
 */
const inputMixin = css<InputMixinProps>`
  background: ${({ theme }) => theme.color.background.primary};
  color: ${({ theme }) => theme.color.text.primary};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.color.border.input};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.element.borderRadius};

  width: 100%;
  min-width: 0;

  text-decoration: none;
  outline: 0 !important;
  transition: ${TransitionDuration.Fast};

  /* Some input-likes like <select/> reduce opacity when disabled */
  opacity: 1;

  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  &${isDisabled} {
    background: ${({ theme }) => theme.color.background.disabled};
    border-color: ${({ theme }) => theme.color.border.disabled};
    color: ${({ theme }) => theme.color.text.disabled};
    ::placeholder {
      color: ${({ theme }) => theme.color.text.disabled};
    }
  }

  &${isHovered}${isNotDisabled} {
    /**
      * The shadow order is important for the color to transition properly.
      * Shadows transition in the order they are specified.
      */
    box-shadow:
      0 0 0 1px transparent,
      ${Elevation.Mid};
  }

  ${({ $size }) => {
    switch ($size) {
      default:
      case 'md':
        return css`
          height: ${({ theme }) => theme.element.height.xs};
          padding-top: ${Spacing[2]};
          padding-right: ${Spacing[3]};
          padding-bottom: ${Spacing[2]};
          padding-left: ${Spacing[3]};
          ${Typography.TextMedium};
        `;
      case 'sm':
        return css`
          height: 24px;
          padding-top: ${Spacing[1]};
          padding-right: ${Spacing[2]};
          padding-bottom: ${Spacing[1]};
          padding-left: ${Spacing[2]};
          ${Typography.TextSmall};
        `;
    }
  }}

  ${({ theme, hasError, hasWarning }) =>
    hasError
      ? css`
          border-color: ${theme.color.variants.danger.default};
          color: ${theme.color.variants.danger.default};

          ${isHovered}${isNotDisabled} {
            box-shadow:
              0 0 0 1px ${theme.color.variants.danger.default},
              ${Elevation.Mid};
          }

          &${isActive}, &${isFocused} {
            box-shadow: 0 0 0 1px ${theme.color.variants.danger.default};
          }
        `
      : hasWarning
        ? css`
            border-color: ${theme.color.variants.caution.default};
            color: ${theme.color.variants.caution.default};

            ${isHovered}${isNotDisabled} {
              box-shadow:
                0 0 0 1px ${theme.color.variants.caution.default},
                ${Elevation.Mid};
            }

            &${isActive}, &${isFocused} {
              box-shadow: 0 0 0 1px ${theme.color.variants.caution.default};
            }
          `
        : css`
            border-color: ${theme.color.border.input};
            color: ${theme.color.text.primary};

            &${isActive}, &${isFocused} {
              border-color: ${theme.color.border.active};

              ${isHovered}${isNotDisabled} {
                box-shadow:
                  0 0 0 1px ${theme.color.border.active},
                  ${Elevation.Mid};
              }
            }
          `}

  ${printMixins.hide}
`;

export default inputMixin;
